/**
 * Keycloak Authentication Utilities
 *
 * Single-responsibility functions for all Keycloak auth operations:
 * redirects, token parsing, token refresh, introspection, and simple getters.
 */

import { FRONT_END_SERVER_HOST, HOST, SSO_TOKEN, REFURB_TOKEN, CAT_TOKEN } from ".";

const API_URL = `${HOST}/api`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convert a plain object to URL-encoded form data. */
const toFormData = (data: Record<string, string>): URLSearchParams => {
  const formData = new URLSearchParams();
  for (const [key, value] of Object.entries(data)) {
    formData.append(key, value);
  }
  return formData;
};

// ---------------------------------------------------------------------------
// Redirects
// ---------------------------------------------------------------------------

/**
 * Redirect the browser to the Keycloak login page.
 * After authentication Keycloak will redirect back to `continueUrl`.
 */
export const redirectToKeycloakLogin = (continueUrl?: string): void => {
  // NOTE: Do NOT use encodeURIComponent — the backend expects the raw URL
  const target = continueUrl
    ? `${API_URL}/login?continue=${continueUrl}`
    : `${API_URL}/login`;
  window.location.href = target;
};

/**
 * Redirect the browser to the Keycloak logout endpoint.
 * After session termination Keycloak will redirect back to `continueUrl`.
 */
export const redirectToKeycloakLogout = (continueUrl?: string): void => {
  // NOTE: Do NOT use encodeURIComponent — the backend expects the raw URL
  const target = continueUrl
    ? `${API_URL}/logout?continue=${continueUrl}`
    : `${API_URL}/logout`;
  window.location.href = target;
};

// ---------------------------------------------------------------------------
// Token helpers
// ---------------------------------------------------------------------------

/** Read the current SSO access token from localStorage (may be `null`). */
export const getAccessToken = (): string | null =>
  localStorage.getItem(SSO_TOKEN);

/** Read the current refresh token from localStorage (may be `null`). */
export const getRefreshToken = (): string | null =>
  localStorage.getItem("authTokenRefresh");

/** Quick check: does the user currently have a stored SSO token? */
export const isAuthenticated = (): boolean => !!getAccessToken();

// ---------------------------------------------------------------------------
// Parse tokens from URL (after Keycloak redirect)
// ---------------------------------------------------------------------------

export interface ParsedTokens {
  accessToken: string | null;
  refreshToken: string | null;
  /** true when fresh tokens were found in the URL (not just localStorage) */
  freshFromUrl: boolean;
}

/**
 * Extract `token` and `refresh` query-params that Keycloak appends to the
 * redirect URL.  When present they are saved to localStorage and the URL is
 * cleaned up via `history.replaceState` so a page refresh won't re-process
 * the same tokens.
 *
 * Falls back to whatever is already in localStorage if the URL carries no
 * tokens.
 */
export const parseTokensFromUrl = (): ParsedTokens => {
  const params = new URLSearchParams(window.location.search);
  const tokenFromUrl = params.get("token");
  const refreshFromUrl = params.get("refresh");
  const freshFromUrl = !!(tokenFromUrl && refreshFromUrl);

  // Persist fresh tokens
  if (tokenFromUrl) localStorage.setItem(SSO_TOKEN, tokenFromUrl);
  if (refreshFromUrl) localStorage.setItem("authTokenRefresh", refreshFromUrl);

  // Clean the URL so tokens don't linger in the address bar
  if (freshFromUrl) {
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, "", cleanUrl);
  }

  return {
    accessToken: tokenFromUrl || localStorage.getItem(SSO_TOKEN),
    refreshToken: refreshFromUrl || localStorage.getItem("authTokenRefresh"),
    freshFromUrl,
  };
};

// ---------------------------------------------------------------------------
// Token refresh
// ---------------------------------------------------------------------------

export interface TokenPair {
  access_token: string;
  refresh_token: string;
}

/**
 * Refresh the SSO access token using the stored refresh token.
 *
 * On success the new tokens are persisted to localStorage and returned.
 * On failure an error is thrown — callers should catch and redirect to login.
 */
export const refreshAccessToken = async (): Promise<TokenPair> => {
  const currentAccessToken = getAccessToken() || "";
  const currentRefreshToken = getRefreshToken();

  if (!currentRefreshToken) {
    throw new Error("No refresh token available");
  }

  const formData = toFormData({
    grant_type: "refresh_token",
    refresh_token: currentRefreshToken,
  });

  const response = await fetch(`${API_URL}/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      "Keycloak-Authorization": currentAccessToken
        ? `bearer ${currentAccessToken}`
        : "",
    },
    body: formData,
  });

  const json = await response.json();

  if (json?.status === "success") {
    const { access_token, refresh_token } = json.message;
    localStorage.setItem(SSO_TOKEN, access_token);
    localStorage.setItem("authTokenRefresh", refresh_token);

    // Keep derived token stores in sync
    if (localStorage.getItem(REFURB_TOKEN))
      localStorage.setItem(REFURB_TOKEN, access_token);
    if (localStorage.getItem(CAT_TOKEN))
      localStorage.setItem(CAT_TOKEN, access_token);

    return { access_token, refresh_token };
  }

  throw new Error(`Token refresh failed: ${json?.status ?? "unknown"}`);
};

// ---------------------------------------------------------------------------
// Token introspection
// ---------------------------------------------------------------------------

/**
 * Ask the backend whether `accessToken` is still valid.
 * Returns `true` when the server confirms the token, `false` otherwise.
 */
export const introspectToken = async (
  accessToken: string
): Promise<boolean> => {
  try {
    const response = await fetch(`${API_URL}/introspect`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "Keycloak-Authorization": `bearer ${accessToken}`,
      },
      body: "",
    });
    const json = await response.json();
    return json?.status === "success";
  } catch {
    return false;
  }
};
